import type { Texture } from 'pixi.js';
import { AnimationAtlasCache } from './animation-atlas-cache';
import { tryResolveGameAssetFile } from '@/game/assets/resolveGameAssetUrl';
import bakedManifest from '../../../assets/world-art/animations/individual-idles/manifest.json';
import { NPCIdleScheduler } from './npc-idle-scheduler';

export type IndividualIdleClip = 'breathing_idle' | 'catwalk_idle' | 'idle' | 'bored' | 'looking_around';
export interface BakedCharacter {
  character: string; file: string; width: number; height: number;
  framesPerClip: number; fps: number; clipOrder: IndividualIdleClip[];
  clips?: Partial<Record<IndividualIdleClip, { frameCount: number; frameDurationMs: number; file?: string; columns?: number; rows?: number }>>;
}
interface IndividualIdleManifest {
  characters: BakedCharacter[];
  roleBindings: {
    cityNpcRoles: Record<string, { modelFamily: string }>;
    heroModelVariants: Record<string, string>;
    townRoleAliases: Record<string, string>;
    roadNpcRoles: Record<string, string>;
    roadEvents: Record<string, { npcs?: Array<{ nameKey?: string; modelRole?: string }> }>;
  };
}
const baked = bakedManifest as unknown as IndividualIdleManifest;
const characters = new Map(baked.characters.map(character => [character.character, character]));
const { roadNpcRoles: roadRoles, townRoleAliases: townAliases, cityNpcRoles: townRoles, roadEvents } = baked.roleBindings;

/** Metadata for the baked animation sheet used by town/editor sprite inspectors. */
export function individualIdleCharacter(characterId: string): BakedCharacter | undefined {
  return characters.get(characterId);
}

/** Camera calibration bakes adult/child stature into a shared 96x128 frame. */
export function individualNpcIdleScale(_characterId: string): number {
  return 1;
}

export function stableHeroIdleCharacter(modelVariant: number): string | null {
  const variant = Number.isInteger(modelVariant) && modelVariant >= 0 && modelVariant <= 3 ? modelVariant : 0;
  const character = (baked.roleBindings.heroModelVariants ?? {
    '0': 'hero_warrior_male', '1': 'hero_ranger_female',
    '2': 'hero_bounty_hunter', '3': 'hero_warrior_female',
  })[String(variant)];
  return character && characters.has(character) ? character : null;
}

export function stableIndividualIdleCharacter(id: number, type: string, nameKey?: string, townId?: number): string | null {
  if (nameKey?.startsWith('npc.')) {
    for (const record of Object.values(roadEvents)) {
      const npc = record.npcs?.find(item => item.nameKey === nameKey);
      const eventCharacter = npc?.modelRole && roadCharacter(npc.modelRole, id);
      if (eventCharacter) return eventCharacter;
    }
  }
  const alias = townAliases[type] ?? (type === 'crier' ? 'herald' : type);
  const family = townRoles[alias]?.modelFamily;
  const variantSeed = townId ?? id;
  if (family) return `${family}_v${Math.abs(variantSeed) % 3 + 1}`;
  return roadCharacter(type, id) ?? `merchant_v${Math.abs(variantSeed) % 3 + 1}`;
}
function roadCharacter(role: string, id: number): string | null {
  // Children are reserved for persisted ambient inhabitants, never services/events.
  if (role === 'road_child_boy' || role === 'road_child_girl') return 'merchant_v1';
  const file = roadRoles[role];
  if (!file) return null;
  const modelFamily = file.match(/^(.+)_v\{1\.\.3\}/)?.[1];
  const variant = modelFamily ? `${modelFamily}_v${Math.abs(id) % 3 + 1}` : file.replace(/_rigged\.fbx$/, '');
  return characters.has(variant) ? variant : null;
}

const actorClips = new Map<string, { startedAt: number; clipIndex: number }>();
const npcClips = new Map<string, { characterId: string; scheduler: NPCIdleScheduler }>();
export function resetIndividualIdleAnimations(): void { actorClips.clear(); npcClips.clear(); }
export function individualIdleFrame(now: number, actorKey: string, characterId: string, moving: boolean, isReady?: (clip: IndividualIdleClip) => boolean): { character: BakedCharacter; clip: IndividualIdleClip; frame: number } | null {
  if (moving) { actorClips.delete(actorKey); npcClips.delete(actorKey); return null; }
  const character = characters.get(characterId);
  if (!character) return null;
  if (character.clips) {
    let state = npcClips.get(actorKey);
    if (!state || state.characterId !== characterId) {
      const scheduler = new NPCIdleScheduler(actorKey, clip => {
        const timing = character.clips![clip]!;
        return timing.frameCount * timing.frameDurationMs;
      });
      state = { characterId, scheduler }; npcClips.set(actorKey, state);
    }
    const { clip, elapsedMs } = state.scheduler.sample(now, isReady);
    const timing = character.clips[clip]!;
    return { character, clip, frame: Math.floor((elapsedMs + 1e-7) / timing.frameDurationMs) % timing.frameCount };
  }
  const clipOrder = character.clipOrder.filter(clip => clip !== 'breathing_idle');
  let state = actorClips.get(actorKey);
  if (!state) {
    let hash = 0;
    for (let i = 0; i < actorKey.length; i++) hash = (hash * 31 + actorKey.charCodeAt(i)) | 0;
    state = { startedAt: now, clipIndex: Math.abs(hash) % clipOrder.length };
    actorClips.set(actorKey, state);
  } else if (now - state.startedAt >= 20_000) {
    state = { startedAt: now, clipIndex: (state.clipIndex + 1) % clipOrder.length };
    actorClips.set(actorKey, state);
  }
  const clip = clipOrder[state.clipIndex]!;
  const frame = Math.floor(Math.max(0, now - state.startedAt) / (1000 / character.fps)) % character.framesPerClip;
  return { character, clip, frame };
}

/** Dense NPC sheets are per clip; the four legacy hero sheets remain multi-row. */
export function individualIdleTextureIndex(character: BakedCharacter, clip: IndividualIdleClip, frame: number): number {
  return character.clips?.[clip]?.file ? frame : character.clipOrder.indexOf(clip) * character.framesPerClip + frame;
}

export class IndividualIdleAssets extends AnimationAtlasCache {
  character(characterId: string): BakedCharacter | undefined { return characters.get(characterId); }
  get(characterId: string, clip: IndividualIdleClip = 'catwalk_idle'): readonly Texture[] | null {
    const character = characters.get(characterId), definition = character?.clips?.[clip];
    const url = character && tryResolveGameAssetFile(definition?.file ?? character.file);
    if (!character || !url) return null;
    return this.requestAtlas({ url, width: character.width, height: character.height, columns: definition?.columns ?? character.framesPerClip,
      rows: definition?.rows ?? character.clipOrder.length, scaleMode: 'linear' });
  }
  /** Allows a single-frame preview to wait for the same approved frames as the live game. */
  async preload(characterIds: readonly string[]): Promise<void> {
    for (const id of new Set(characterIds)) {
      this.get(id);
    }
    await this.waitForLoads();
  }
}
